// FILE: src/store/Slices/CopilotSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  cancelCopilotAction,
  cancelCopilotActionDraft,
  cancelCopilotGoal,
  confirmCopilotAction,
  createCopilotSession,
  editCopilotAction,
  deleteCopilotSession,
  fetchActiveCopilotGoal,
  fetchCopilotGoal,
  fetchCopilotMessages,
  fetchCopilotSessions,
  patchCopilotActionDraft,
  renameCopilotSession,
  sendCopilotChat,
  startCopilotReadResultAction,
} from "../../services/copilot";

const getSessionId = (session) => {
  return session?._id || session?.id || session?.sessionId || null;
};

const getMessageId = (message) => {
  return message?._id || message?.id || null;
};

export const createCopilotSessionThunk = createAsyncThunk(
  "copilot/createSession",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCopilotSession(payload);
      return response.session;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCopilotSessionsThunk = createAsyncThunk(
  "copilot/fetchSessions",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const response = await fetchCopilotSessions(payload);

      return {
        items: response.items || [],
        pagination: response.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0,
        },
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCopilotMessagesThunk = createAsyncThunk(
  "copilot/fetchMessages",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await fetchCopilotMessages(sessionId);

      return {
        session: response.session || null,
        messages: response.messages || [],
        goal: response.goal || null,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const renameCopilotSessionThunk = createAsyncThunk(
  "copilot/renameSession",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await renameCopilotSession(payload);
      return response.session;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCopilotSessionThunk = createAsyncThunk(
  "copilot/deleteSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await deleteCopilotSession(sessionId);
      return {
        sessionId: response.sessionId || sessionId,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const sendCopilotChatThunk = createAsyncThunk(
  "copilot/sendChat",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await sendCopilotChat(payload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.message,
        statusCode: error.statusCode || null,
        code: error.code || null,
        errorType: error.errorType || null,
        feature: error.feature || null,
        requiredCredits: error.requiredCredits,
        remainingCredits: error.remainingCredits,
        addCreditsPath: error.addCreditsPath || null,
      });
    }
  }
);

export const fetchActiveCopilotGoalThunk =
  createAsyncThunk(
    "copilot/fetchActiveGoal",
    async (
      sessionId,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await fetchActiveCopilotGoal(
            sessionId
          );

        return {
          sessionId,
          goal:
            response.goal ||
            null,
        };
      } catch (error) {
        return rejectWithValue(
          error.message
        );
      }
    }
  );

export const fetchCopilotGoalThunk =
  createAsyncThunk(
    "copilot/fetchGoal",
    async (
      payload,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await fetchCopilotGoal(
            payload
          );

        return {
          sessionId:
            payload.sessionId,
          goal:
            response.goal ||
            null,
        };
      } catch (error) {
        return rejectWithValue(
          error.message
        );
      }
    }
  );

export const cancelCopilotGoalThunk =
  createAsyncThunk(
    "copilot/cancelGoal",
    async (
      payload,
      {
        rejectWithValue,
      }
    ) => {
      try {
        return await cancelCopilotGoal(
          payload
        );
      } catch (error) {
        return rejectWithValue(
          error.message
        );
      }
    }
  );

export const startCopilotReadResultActionThunk =
createAsyncThunk(
"copilot/startReadResultAction",
async (
payload,
{
rejectWithValue,
}
) => {
try {
return await startCopilotReadResultAction(
payload
);
} catch (error) {
return rejectWithValue(
error.message
);
}
}
);

export const patchCopilotActionDraftThunk = createAsyncThunk(
  "copilot/patchActionDraft",
  async (payload, { rejectWithValue }) => {
    try {
      return await patchCopilotActionDraft(
        payload
      );
    } catch (error) {
      return rejectWithValue(
        error.message
      );
    }
  }
);

export const cancelCopilotActionDraftThunk = createAsyncThunk(
  "copilot/cancelActionDraft",
  async (payload, { rejectWithValue }) => {
    try {
      return await cancelCopilotActionDraft(
        payload
      );
    } catch (error) {
      return rejectWithValue(
        error.message
      );
    }
  }
);

export const confirmCopilotActionThunk = createAsyncThunk(
"copilot/confirmAction",
async (payload, { rejectWithValue }) => {
try {
return await confirmCopilotAction(payload);
} catch (error) {
return rejectWithValue({
message:
error.message,
goal:
error.data?.goal ||
null,
});
}
}
);

export const editCopilotActionThunk =
createAsyncThunk(
"copilot/editAction",
async (
payload,
{
rejectWithValue,
}
) => {
try {
return await editCopilotAction(
payload
);
} catch (error) {
return rejectWithValue(
error.message
);
}
}
);


export const cancelCopilotActionThunk = createAsyncThunk(
  "copilot/cancelAction",
  async (payload, { rejectWithValue }) => {
    try {
      return await cancelCopilotAction(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  sessions: [],
  sessionsPagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
  currentSession: null,
  currentSessionId: null,
  messages: [],
  sessionsStatus: "idle",
  messagesStatus: "idle",
  chatStatus: "idle",
  createSessionStatus: "idle",
  actionStatus: "idle",
  activeActionId: null,
  actionDraftStatus: "idle",
  activeActionDraftId: null,
  currentActionDraft: null,
  goal: null,
  goalStatus: "idle",
  goalError: null,
  error: null,

  lastKnowledge: {
    enabled: false,
    mode: null,
    count: 0,
    error: null,
    sourceReferences: [],
  },
  lastAttachments: {
    enabled: false,
    count: 0,
    sourceReferences: [],
  },
  lastModel: null,
  lastUsage: null,
  lastPricing: null,
  lastGuardrails: null,
};

const upsertSession = (sessions, session) => {
  const sessionId = getSessionId(session);

  if (!sessionId) {
    return sessions;
  }

  const existingIndex = sessions.findIndex((item) => {
    return String(getSessionId(item)) === String(sessionId);
  });

  if (existingIndex === -1) {
    return [session, ...sessions];
  }

  const nextSessions = [...sessions];

  nextSessions[existingIndex] = {
    ...nextSessions[existingIndex],
    ...session,
  };

  return nextSessions.sort((a, b) => {
    const leftDate = new Date(a.lastMessageAt || a.updatedAt || a.createdAt || 0);
    const rightDate = new Date(b.lastMessageAt || b.updatedAt || b.createdAt || 0);

    return rightDate - leftDate;
  });
};

const mergeMessages = (existingMessages, incomingMessages) => {
  const mergedById = new Map();

  existingMessages.forEach((message) => {
    const id = getMessageId(message);

    if (id) {
      mergedById.set(String(id), message);
    }
  });

  incomingMessages.forEach((message) => {
    const id = getMessageId(message);

    if (id) {
      mergedById.set(String(id), message);
    }
  });

  return Array.from(mergedById.values()).sort((a, b) => {
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  });
};

const updatePendingActionInMessages = (
  messages,
  updatedAction
) => {
  const actionId =
    updatedAction?._id ||
    updatedAction?.id ||
    null;

  if (!actionId) {
    return messages;
  }

  return messages.map((message) => {
    const pendingAction =
      message.metadata?.pendingAction;

    const pendingActionId =
      pendingAction?._id ||
      pendingAction?.id ||
      null;

    if (
      !pendingActionId ||
      String(pendingActionId) !==
        String(actionId)
    ) {
      return message;
    }

    return {
      ...message,
      metadata: {
        ...(message.metadata || {}),
        pendingAction: {
          ...pendingAction,
          ...updatedAction,
          confirmationToken: null,
        },
        writeAction: {
          ...(message.metadata?.writeAction || {}),
          status:
            updatedAction.status ||
            message.metadata?.writeAction?.status ||
            null,
        },
      },
    };
  });
};

const applyActionDraftResponse = (
  state,
  response = {}
) => {
  const session =
    response.session || null;

  const actionDraft =
    response.actionDraft || null;

  const responseMessages =
    Array.isArray(response.messages)
      ? response.messages
      : response.assistantMessage
      ? [response.assistantMessage]
      : [];

  state.currentSession =
    session || state.currentSession;

  state.currentSessionId =
    getSessionId(session) ||
    state.currentSessionId;

  state.sessions = upsertSession(
    state.sessions,
    session
  );

  state.messages = mergeMessages(
    state.messages,
    responseMessages
  );

  state.currentActionDraft =
    actionDraft;

  state.activeActionDraftId =
    actionDraft &&
    [
      "collecting_requirements",
      "ready_for_confirmation",
      "finalizing",
    ].includes(actionDraft.status)
      ? actionDraft.id ||
        actionDraft._id ||
        null
      : null;

  state.lastKnowledge =
    response.knowledge ||
    state.lastKnowledge;

  state.lastAttachments =
    response.attachments ||
    state.lastAttachments;

  state.lastModel =
    response.model ||
    state.lastModel;

  if (
    Object.prototype.hasOwnProperty.call(
      response,
      "goal"
    )
  ) {
    state.goal =
      response.goal || null;
    state.goalStatus =
      "succeeded";
    state.goalError =
      null;
  }
};

const applyActionResponse = (
  state,
  response = {}
) => {
  const session = response.session || null;
  const action = response.action || null;
  const responseMessages =
    response.messages ||
    (
      response.assistantMessage
        ? [response.assistantMessage]
        : []
    );

  state.currentSession =
    session || state.currentSession;

  state.currentSessionId =
    getSessionId(session) ||
    state.currentSessionId;

  state.sessions =
    upsertSession(
      state.sessions,
      session
    );

  state.messages =
    updatePendingActionInMessages(
      state.messages,
      action
    );

  state.messages =
    mergeMessages(
      state.messages,
      responseMessages
    );

  if (
    Object.prototype.hasOwnProperty.call(
      response,
      "goal"
    )
  ) {
    state.goal =
      response.goal || null;
    state.goalStatus =
      "succeeded";
    state.goalError =
      null;
  }
};

const copilotSlice = createSlice({
  name: "copilot",
  initialState,
  reducers: {
    clearCopilotError(state) {
      state.error = null;
    },
    clearCopilotConversation(state) {
      state.currentSession = null;
      state.currentSessionId = null;
      state.messages = [];
      state.messagesStatus = "idle";
      state.chatStatus = "idle";
      state.actionStatus = "idle";
      state.activeActionId = null;
      state.actionDraftStatus = "idle";
      state.activeActionDraftId = null;
      state.currentActionDraft = null;
      state.goal = null;
      state.goalStatus = "idle";
      state.goalError = null;
      state.error = null;
      state.lastKnowledge = initialState.lastKnowledge;
      state.lastAttachments = initialState.lastAttachments;
      state.lastModel = null;
            state.lastUsage = null;
      state.lastPricing = null;
      state.lastGuardrails = null;
    },
    setCurrentCopilotSession(state, action) {
      const session = action.payload || null;

      state.currentSession = session;
      state.currentSessionId = getSessionId(session);
      state.messages = [];
      state.messagesStatus = "idle";
      state.actionStatus = "idle";
      state.activeActionId = null;
      state.actionDraftStatus = "idle";
      state.activeActionDraftId = null;
      state.currentActionDraft = null;
      state.goal = null;
      state.goalStatus = "idle";
      state.goalError = null;
      state.error = null;
      state.lastKnowledge = initialState.lastKnowledge;
      state.lastAttachments = initialState.lastAttachments;
      state.lastModel = null;
            state.lastUsage = null;
      state.lastPricing = null;
      state.lastGuardrails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCopilotSessionThunk.pending, (state) => {
        state.createSessionStatus = "loading";
        state.error = null;
      })
      .addCase(createCopilotSessionThunk.fulfilled, (state, action) => {
        state.createSessionStatus = "succeeded";
        state.currentSession = action.payload || null;
        state.currentSessionId = getSessionId(action.payload);
        state.sessions = upsertSession(state.sessions, action.payload);
      })
      .addCase(createCopilotSessionThunk.rejected, (state, action) => {
        state.createSessionStatus = "failed";
        state.error = action.payload || "Failed to create Copilot session.";
      })
      .addCase(fetchCopilotSessionsThunk.pending, (state) => {
        state.sessionsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchCopilotSessionsThunk.fulfilled, (state, action) => {
        state.sessionsStatus = "succeeded";
        state.sessions = action.payload.items || [];
        state.sessionsPagination = action.payload.pagination;
      })
      .addCase(fetchCopilotSessionsThunk.rejected, (state, action) => {
        state.sessionsStatus = "failed";
        state.error = action.payload || "Failed to fetch Copilot sessions.";
      })
      .addCase(fetchCopilotMessagesThunk.pending, (state) => {
        state.messagesStatus = "loading";
        state.error = null;
      })
      .addCase(fetchCopilotMessagesThunk.fulfilled, (state, action) => {
        const session = action.payload.session || null;

        state.messagesStatus = "succeeded";
        state.currentSession = session;
        state.currentSessionId = getSessionId(session);
        state.messages = action.payload.messages || [];
        state.sessions = upsertSession(state.sessions, session);
        state.goal = action.payload.goal || null;
        state.goalStatus = "succeeded";
        state.goalError = null;
      })
      .addCase(fetchCopilotMessagesThunk.rejected, (state, action) => {
        state.messagesStatus = "failed";
        state.error = action.payload || "Failed to fetch Copilot messages.";
      })
      .addCase(renameCopilotSessionThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(renameCopilotSessionThunk.fulfilled, (state, action) => {
        const session = action.payload || null;
        const sessionId = getSessionId(session);

        if (!sessionId) return;

        state.sessions = upsertSession(state.sessions, session);

        if (String(state.currentSessionId || "") === String(sessionId)) {
          state.currentSession = {
            ...(state.currentSession || {}),
            ...session,
          };
        }
      })
      .addCase(renameCopilotSessionThunk.rejected, (state, action) => {
        state.error = action.payload || "Failed to rename Copilot session.";
      })
      .addCase(deleteCopilotSessionThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteCopilotSessionThunk.fulfilled, (state, action) => {
        const deletedSessionId = action.payload?.sessionId;

        state.sessions = state.sessions.filter((session) => {
          return String(getSessionId(session)) !== String(deletedSessionId);
        });

        if (String(state.currentSessionId || "") === String(deletedSessionId || "")) {
          state.currentSession = null;
          state.currentSessionId = null;
          state.messages = [];
          state.messagesStatus = "idle";
          state.chatStatus = "idle";
          state.actionStatus = "idle";
          state.activeActionId = null;
          state.actionDraftStatus = "idle";
          state.activeActionDraftId = null;
          state.currentActionDraft = null;
          state.goal = null;
          state.goalStatus = "idle";
          state.goalError = null;
          state.lastKnowledge = initialState.lastKnowledge;
          state.lastAttachments = initialState.lastAttachments;
          state.lastModel = null;
                    state.lastUsage = null;
          state.lastPricing = null;
          state.lastGuardrails = null;
        }
      })
      .addCase(deleteCopilotSessionThunk.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete Copilot session.";
      })
      .addCase(sendCopilotChatThunk.pending, (state) => {
        state.chatStatus = "loading";
        state.error = null;
      })
      .addCase(sendCopilotChatThunk.fulfilled, (state, action) => {
const response = action.payload || {};
const responseGoal =
response.goal ||
null;
const responseGoalId =
responseGoal?.id ||
responseGoal?._id ||
null;
const rawResponseMessages =
Array.isArray(
response.messages
)
? response.messages
: [];
const lastAssistantIndex =
rawResponseMessages.reduce(
(lastIndex, message, index) =>
message?.role ===
"assistant"
? index
: lastIndex,
-1
);
const responseMessages =
rawResponseMessages.map(
(message, index) => {
if (
!responseGoal ||
message?.role !==
"assistant"
) {
return message;
}

const messageGoalId =
message.goalId ||
message.metadata?.goalId ||
null;
const matchesGoal =
responseGoalId &&
messageGoalId &&
String(messageGoalId) ===
String(responseGoalId);
const isLastAssistant =
index ===
lastAssistantIndex;

if (
!matchesGoal &&
!isLastAssistant
) {
return message;
}

return {
...message,
goalId:
messageGoalId ||
responseGoalId,
metadata: {
...(message.metadata || {}),
goal:
responseGoal,
},
};
}
);
const session = response.session || null;

state.chatStatus = "succeeded";
        state.currentSession = session || state.currentSession;
        state.currentSessionId = getSessionId(session) || state.currentSessionId;
        state.sessions = upsertSession(state.sessions, session);
        state.messages = mergeMessages(state.messages, responseMessages);
        state.lastKnowledge = response.knowledge || initialState.lastKnowledge;
        state.lastAttachments = response.attachments || initialState.lastAttachments;
                state.lastModel = response.model || null;
        state.lastUsage = response.usage || null;
        state.lastPricing = response.pricing || null;
        state.lastGuardrails = response.guardrails || null;

        if (
          Object.prototype.hasOwnProperty.call(
            response,
            "goal"
          )
        ) {
          state.goal =
            response.goal || null;
          state.goalStatus =
            "succeeded";
          state.goalError = null;
        }

        if (response.actionDraft) {
          state.currentActionDraft =
            response.actionDraft;

          state.activeActionDraftId =
            [
              "collecting_requirements",
              "ready_for_confirmation",
              "finalizing",
            ].includes(
              response.actionDraft.status
            )
              ? response.actionDraft.id ||
                response.actionDraft._id ||
                null
              : null;
        }
      })
      .addCase(sendCopilotChatThunk.rejected, (state, action) => {
        state.chatStatus = "failed";
        state.error =
          action.payload || {
            message:
              action.error?.message ||
              "Copilot chat failed.",
          };
      })

      .addCase(
startCopilotReadResultActionThunk.pending,
(state) => {
state.actionDraftStatus =
"loading";
state.error = null;
}
)
.addCase(
startCopilotReadResultActionThunk.fulfilled,
(state, action) => {
state.actionDraftStatus =
"succeeded";

applyActionDraftResponse(
state,
action.payload || {}
);
}
)
.addCase(
startCopilotReadResultActionThunk.rejected,
(state, action) => {
state.actionDraftStatus =
"failed";

state.error =
action.payload ||
"Failed to start the Brand Kit update.";
}
)
      .addCase(
        patchCopilotActionDraftThunk.pending,
        (state, action) => {
          state.actionDraftStatus =
            "loading";

          state.activeActionDraftId =
            action.meta.arg
              ?.actionDraftId ||
            state.activeActionDraftId;

          state.error = null;
        }
      )
      .addCase(
        patchCopilotActionDraftThunk.fulfilled,
        (state, action) => {
          state.actionDraftStatus =
            "succeeded";

          applyActionDraftResponse(
            state,
            action.payload || {}
          );
        }
      )
      .addCase(
        patchCopilotActionDraftThunk.rejected,
        (state, action) => {
          state.actionDraftStatus =
            "failed";

          state.error =
            action.payload ||
            "Failed to update the Copilot action.";
        }
      )
      .addCase(
        cancelCopilotActionDraftThunk.pending,
        (state, action) => {
          state.actionDraftStatus =
            "loading";

          state.activeActionDraftId =
            action.meta.arg
              ?.actionDraftId ||
            state.activeActionDraftId;

          state.error = null;
        }
      )
      .addCase(
        cancelCopilotActionDraftThunk.fulfilled,
        (state, action) => {
          state.actionDraftStatus =
            "succeeded";

          applyActionDraftResponse(
            state,
            action.payload || {}
          );

          state.activeActionDraftId =
            null;
        }
      )
      .addCase(
        cancelCopilotActionDraftThunk.rejected,
        (state, action) => {
          state.actionDraftStatus =
            "failed";

          state.error =
            action.payload ||
            "Failed to cancel the Copilot action.";
        }
      )
      .addCase(confirmCopilotActionThunk.pending, (state, action) => {
        state.actionStatus = "loading";
        state.activeActionId =
          action.meta.arg?.actionId ||
          null;
        state.error = null;
      })
      .addCase(confirmCopilotActionThunk.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        state.activeActionId = null;
        applyActionResponse(
          state,
          action.payload || {}
        );
      })
      .addCase(confirmCopilotActionThunk.rejected, (state, action) => {
state.actionStatus = "failed";
state.activeActionId = null;
state.error =
action.payload?.message ||
action.error?.message ||
"Failed to confirm the Copilot action.";

if (
action.payload?.goal
) {
state.goal =
action.payload.goal;
state.goalStatus =
"succeeded";
state.goalError =
null;
}
})
      .addCase(
        editCopilotActionThunk.pending,
        (state, action) => {
        state.actionStatus =
        "loading";
        state.activeActionId =
        action.meta.arg?.actionId ||
        null;
        state.error = null;
        }
        )
        .addCase(
        editCopilotActionThunk.fulfilled,
        (state, action) => {
        state.actionStatus =
        "succeeded";
        state.activeActionId =
        null;

        applyActionDraftResponse(
        state,
        action.payload || {}
        );
        }
        )
        .addCase(
        editCopilotActionThunk.rejected,
        (state, action) => {
        state.actionStatus =
        "failed";
        state.activeActionId =
        null;
        state.error =
        action.payload ||
        "Failed to reopen the Copilot action for editing.";
        }
        )


      .addCase(cancelCopilotActionThunk.pending, (state, action) => {
        state.actionStatus = "loading";
        state.activeActionId =
          action.meta.arg?.actionId ||
          null;
        state.error = null;
      })
      .addCase(cancelCopilotActionThunk.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        state.activeActionId = null;
        applyActionResponse(
          state,
          action.payload || {}
        );
      })
      .addCase(cancelCopilotActionThunk.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.activeActionId = null;
        state.error =
          action.payload ||
          "Failed to cancel the Copilot action.";
      })
      .addCase(
        fetchActiveCopilotGoalThunk.pending,
        (state) => {
          state.goalStatus =
            "loading";
          state.goalError = null;
        }
      )
      .addCase(
        fetchActiveCopilotGoalThunk.fulfilled,
        (state, action) => {
          if (
            String(
              state.currentSessionId ||
              ""
            ) !==
            String(
              action.payload
                ?.sessionId ||
              ""
            )
          ) {
            return;
          }

          state.goal =
            action.payload?.goal ||
            null;
          state.goalStatus =
            "succeeded";
          state.goalError = null;
        }
      )
      .addCase(
        fetchActiveCopilotGoalThunk.rejected,
        (state, action) => {
          state.goalStatus =
            "failed";
          state.goalError =
            action.payload ||
            "Failed to load the active Copilot goal.";
        }
      )
      .addCase(
        fetchCopilotGoalThunk.pending,
        (state) => {
          state.goalStatus =
            "loading";
          state.goalError = null;
        }
      )
      .addCase(
        fetchCopilotGoalThunk.fulfilled,
        (state, action) => {
          if (
            String(
              state.currentSessionId ||
              ""
            ) !==
            String(
              action.payload
                ?.sessionId ||
              ""
            )
          ) {
            return;
          }

          state.goal =
            action.payload?.goal ||
            null;
          state.goalStatus =
            "succeeded";
          state.goalError = null;
        }
      )
      .addCase(
        fetchCopilotGoalThunk.rejected,
        (state, action) => {
          state.goalStatus =
            "failed";
          state.goalError =
            action.payload ||
            "Failed to load the Copilot goal.";
        }
      )
      .addCase(
        cancelCopilotGoalThunk.pending,
        (state) => {
          state.goalStatus =
            "loading";
          state.goalError = null;
        }
      )
      .addCase(
        cancelCopilotGoalThunk.fulfilled,
        (state, action) => {
          const response =
            action.payload || {};

          state.goal =
            response.goal ||
            null;
          state.goalStatus =
            "succeeded";
          state.goalError = null;

          state.currentSession =
            response.session ||
            state.currentSession;

          state.currentSessionId =
            getSessionId(
              response.session
            ) ||
            state.currentSessionId;

          state.sessions =
            upsertSession(
              state.sessions,
              response.session
            );

          state.messages =
            mergeMessages(
              state.messages,
              Array.isArray(
                response.messages
              )
                ? response.messages
                : response.assistantMessage
                ? [
                    response.assistantMessage,
                  ]
                : []
            );
        }
      )
      .addCase(
        cancelCopilotGoalThunk.rejected,
        (state, action) => {
          state.goalStatus =
            "failed";
          state.goalError =
            action.payload ||
            "Failed to cancel the Copilot goal.";
        }
      );
  },
});

export const {
  clearCopilotConversation,
  clearCopilotError,
  setCurrentCopilotSession,
} = copilotSlice.actions;

export default copilotSlice.reducer;